import { useEffect } from "react";

import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { environmentSelector } from "Reducers/environment";
import {
  isManualDeploymentEnabledSelector,
  loadingDeploymentSettingsSelector,
  updateDeploymentSettingsErrorSelector,
  loadDeploymentSettings
} from "Reducers/environment/deployment";
import { selectFeatureMultiversionDeploymentEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { projectSettingsManualDeploymentSelector } from "Reducers/projectSettings";
import { useAppDispatch, useAppSelector } from "Store/hooks";

export const useDeploymentType = (props?: {
  projectId?: string;
  organizationId?: string;
  environmentId?: string;
}): {
  shouldShowDeploymentType: boolean;
  hasStagedActivities: boolean;
  isManualDeploymentEnabled: boolean;
  isDeploymentSettingsLoading: boolean;
  currentDeploymentType: string;
  updateDeploymentSettingsError?: {
    message: string;
  };
} => {
  const {
    projectId: projectIdParam,
    environmentId: environmentIdParam,
    organizationId: organizationIdParam
  } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId: string;
  }>();

  const environmentId = props?.environmentId || environmentIdParam;
  const organizationId = props?.organizationId || organizationIdParam;
  const projectId = props?.projectId || projectIdParam;
  const environment = useAppSelector(state =>
    environmentSelector(state, {
      environmentId,
      organizationId,
      projectId
    })
  );
  const dispatch = useAppDispatch();

  //vendor level, feature flag to enable multiversion deployment
  const isMultiversionDeploymentEnabled = useAppSelector(
    selectFeatureMultiversionDeploymentEnabled
  );
  //project level, check if the project settings have manual deployment enabled
  const isProjectSettingManualDeployment = useSelectorWithUrlParams(
    projectSettingsManualDeploymentSelector
  );
  //environment level, check if the environment has manual deployment enabled
  const isManualDeploymentEnabled = useAppSelector(state =>
    isManualDeploymentEnabledSelector(state, {
      projectId,
      environmentId,
      organizationId
    })
  );

  useEffect(() => {
    isMultiversionDeploymentEnabled &&
      isProjectSettingManualDeployment &&
      environment?.type !== "development" &&
      environmentId &&
      isManualDeploymentEnabled === undefined &&
      dispatch(
        loadDeploymentSettings({ projectId, environmentId, organizationId })
      );
  }, [
    projectId,
    environment,
    environmentId,
    organizationId,
    isManualDeploymentEnabled,
    isProjectSettingManualDeployment,
    isMultiversionDeploymentEnabled,
    dispatch
  ]);

  const currentDeploymentType = isManualDeploymentEnabled
    ? "manual"
    : "automatic";

  const isDeploymentSettingsLoading = useAppSelector(
    loadingDeploymentSettingsSelector
  );
  const updateDeploymentSettingsError = useAppSelector(state =>
    updateDeploymentSettingsErrorSelector(state, {
      projectId,
      environmentId,
      organizationId
    })
  );

  const hasStagedActivities = environment?.has_staged_activities || false;

  return {
    shouldShowDeploymentType:
      (isMultiversionDeploymentEnabled && isProjectSettingManualDeployment) ||
      false,
    hasStagedActivities,
    isManualDeploymentEnabled,
    isDeploymentSettingsLoading: isDeploymentSettingsLoading || false,
    updateDeploymentSettingsError,
    currentDeploymentType
  };
};
